import { formatDate, parseDate } from './dateUtils'
// Helper function to generate random data
const generateRandomData = () => {
  // Company departments
  const departments = [
    'Engineering',
    'Marketing',
    'Sales',
    'Finance',
    'HR',
    'Product',
    'Operations',
    'Customer Support',
    'Legal',
    'Research',
    'Design',
    'Quality Assurance',
    'IT',
    'Executive',
  ]
  // Engineering positions
  const engineeringPositions = [
    'Software Engineer',
    'Senior Software Engineer',
    'Lead Software Engineer',
    'Frontend Developer',
    'Backend Developer',
    'Full Stack Developer',
    'DevOps Engineer',
    'QA Engineer',
    'Engineering Manager',
    'Technical Lead',
    'System Architect',
    'Mobile Developer',
    'Data Engineer',
  ]
  // Marketing positions
  const marketingPositions = [
    'Marketing Specialist',
    'Marketing Manager',
    'Content Strategist',
    'SEO Specialist',
    'Social Media Manager',
    'Growth Marketer',
    'Brand Manager',
    'Marketing Analyst',
    'Marketing Director',
    'Content Writer',
    'Digital Marketing Specialist',
  ]
  // Sales positions
  const salesPositions = [
    'Sales Representative',
    'Account Executive',
    'Sales Manager',
    'Business Development Rep',
    'Sales Director',
    'Account Manager',
    'Sales Operations',
    'Inside Sales Rep',
    'Enterprise Account Executive',
    'Regional Sales Manager',
  ]
  // Finance positions
  const financePositions = [
    'Financial Analyst',
    'Accountant',
    'Finance Manager',
    'Controller',
    'CFO',
    'Bookkeeper',
    'Payroll Specialist',
    'Tax Accountant',
    'Treasury Analyst',
    'Finance Director',
  ]
  // HR positions
  const hrPositions = [
    'HR Specialist',
    'HR Manager',
    'Recruiter',
    'Talent Acquisition',
    'HR Director',
    'HR Coordinator',
    'Benefits Administrator',
    'People Operations',
    'HR Business Partner',
    'Training Coordinator',
  ]
  // Product positions
  const productPositions = [
    'Product Manager',
    'Product Owner',
    'Senior Product Manager',
    'Product Director',
    'Product Analyst',
    'Product Marketing Manager',
    'Associate Product Manager',
    'Product Operations',
    'VP of Product',
  ]
  // Operations positions
  const operationsPositions = [
    'Operations Manager',
    'Operations Analyst',
    'Operations Coordinator',
    'Operations Director',
    'Logistics Coordinator',
    'Supply Chain Manager',
    'Procurement Specialist',
    'Facilities Manager',
    'Business Operations',
  ]
  // Customer Support positions
  const supportPositions = [
    'Customer Support Representative',
    'Customer Success Manager',
    'Support Specialist',
    'Technical Support',
    'Support Team Lead',
    'Customer Experience Manager',
    'Client Services Representative',
    'Support Operations',
  ]
  // Expense types
  const expenseTypes = [
    'meal',
    'transportation',
    'lodging',
    'office',
    'software',
    'hardware',
    'entertainment',
    'travel',
    'training',
    'other',
  ]
  // Expense subtypes
  const expenseSubtypes = {
    meal: ['breakfast', 'lunch', 'dinner', 'team meal', 'client meal'],
    transportation: [
      'taxi',
      'rideshare',
      'car rental',
      'mileage',
      'parking',
      'airfare',
      'train',
      'bus',
    ],
    lodging: ['hotel', 'airbnb', 'long-term stay'],
    office: ['supplies', 'furniture', 'equipment'],
    software: ['subscription', 'license', 'one-time purchase'],
    hardware: ['computer', 'phone', 'accessories', 'monitor'],
    entertainment: ['client event', 'team event'],
    travel: ['international', 'domestic', 'conference'],
    training: ['course', 'conference', 'certification', 'book'],
    other: ['miscellaneous', 'service', 'fee'],
  }
  // Expense statuses
  const expenseStatuses = ['pending', 'approved', 'rejected']
  // Locations
  const locations = [
    'New York, NY',
    'San Francisco, CA',
    'Chicago, IL',
    'Los Angeles, CA',
    'Austin, TX',
    'Seattle, WA',
    'Boston, MA',
    'Washington, DC',
    'Denver, CO',
    'Atlanta, GA',
    'Miami, FL',
    'Dallas, TX',
    'Portland, OR',
    'Philadelphia, PA',
    'Phoenix, AZ',
    'Remote',
  ]
  // Receipt URLs
  const receiptUrls = [
    'https://placehold.co/600x400/png',
    'https://placehold.co/500x700/png',
    'https://placehold.co/700x500/png',
    'https://placehold.co/400x600/png',
    null,
  ]
  // Rejection reasons
  const rejectionReasons = [
    'Missing receipt',
    'Exceeds allowed amount',
    'Personal expense',
    'Duplicate submission',
    'Insufficient documentation',
    'Not covered by policy',
    'Missing approver',
    'Missing itemization',
    'Incorrect category',
  ]
  // Generate random date within range
  const randomDate = (start, end) => {
    return new Date(
      start.getTime() + Math.random() * (end.getTime() - start.getTime()),
    )
  }
  // Generate random amount within range
  const randomAmount = (min, max, decimals = 2) => {
    const amount = Math.random() * (max - min) + min
    return amount.toFixed(decimals)
  }
  // Generate random phone number
  const randomPhone = () => {
    return `555-${Math.floor(Math.random() * 900 + 100)}-${Math.floor(Math.random() * 9000 + 1000)}`
  }
  // Generate random email
  const randomEmail = (name) => {
    const domains = [
      'example.com',
      'company.com',
      'acme.org',
      'sprinklenet.com',
      'tech.io',
    ]
    const nameParts = name.toLowerCase().split(' ')
    const formats = [
      () =>
        `${nameParts[0]}.${nameParts[nameParts.length - 1]}@${domains[Math.floor(Math.random() * domains.length)]}`,
      () =>
        `${nameParts[0][0]}${nameParts[nameParts.length - 1]}@${domains[Math.floor(Math.random() * domains.length)]}`,
      () =>
        `${nameParts[0]}${nameParts[nameParts.length - 1][0]}@${domains[Math.floor(Math.random() * domains.length)]}`,
      () =>
        `${nameParts[0]}_${nameParts[nameParts.length - 1]}@${domains[Math.floor(Math.random() * domains.length)]}`,
    ]
    return formats[Math.floor(Math.random() * formats.length)]()
  }
  // Get position based on department
  const getPositionForDepartment = (department) => {
    const positionsMap = {
      Engineering: engineeringPositions,
      Marketing: marketingPositions,
      Sales: salesPositions,
      Finance: financePositions,
      HR: hrPositions,
      Product: productPositions,
      Operations: operationsPositions,
      'Customer Support': supportPositions,
    }
    const positions = positionsMap[department] || [
      'Specialist',
      'Coordinator',
      'Manager',
      'Director',
      'Analyst',
    ]
    return positions[Math.floor(Math.random() * positions.length)]
  }
  // Generate random name
  const randomName = () => {
    const firstNames = [
      'John',
      'Jane',
      'Michael',
      'Emily',
      'David',
      'Sarah',
      'Robert',
      'Jennifer',
      'William',
      'Linda',
      'James',
      'Patricia',
      'Richard',
      'Elizabeth',
      'Thomas',
      'Susan',
      'Charles',
      'Jessica',
      'Daniel',
      'Mary',
      'Matthew',
      'Karen',
      'Anthony',
      'Nancy',
      'Mark',
      'Lisa',
      'Donald',
      'Betty',
      'Steven',
      'Dorothy',
      'Paul',
      'Sandra',
      'Andrew',
      'Ashley',
      'Joshua',
      'Kimberly',
      'Kenneth',
      'Donna',
      'Kevin',
      'Carol',
      'Brian',
      'Michelle',
      'George',
      'Amanda',
      'Edward',
      'Melissa',
      'Ronald',
      'Deborah',
      'Timothy',
      'Stephanie',
      'Jason',
      'Rebecca',
      'Jeffrey',
      'Laura',
      'Ryan',
      'Sharon',
      'Jacob',
      'Cynthia',
      'Gary',
      'Kathleen',
      'Nicholas',
      'Amy',
      'Eric',
      'Shirley',
      'Jonathan',
      'Angela',
      'Stephen',
      'Helen',
      'Larry',
      'Anna',
      'Justin',
      'Brenda',
      'Scott',
      'Pamela',
      'Brandon',
      'Nicole',
      'Benjamin',
      'Samantha',
      'Samuel',
      'Katherine',
      'Gregory',
      'Emma',
      'Alexander',
      'Ruth',
      'Frank',
      'Christine',
      'Patrick',
      'Catherine',
      'Raymond',
      'Debra',
      'Jack',
      'Rachel',
      'Dennis',
      'Carolyn',
      'Jerry',
      'Janet',
      'Tyler',
      'Maria',
      'Aaron',
      'Heather',
      'Jose',
      'Diane',
      'Adam',
      'Julie',
      'Henry',
      'Joyce',
      'Nathan',
      'Victoria',
      'Douglas',
      'Kelly',
      'Zachary',
      'Christina',
      'Peter',
      'Lauren',
      'Kyle',
      'Joan',
      'Walter',
      'Evelyn',
      'Ethan',
      'Olivia',
      'Jeremy',
      'Judith',
      'Harold',
      'Megan',
      'Keith',
      'Cheryl',
      'Christian',
      'Martha',
      'Roger',
      'Andrea',
      'Noah',
      'Frances',
      'Gerald',
      'Hannah',
      'Carl',
      'Jacqueline',
      'Terry',
      'Ann',
      'Sean',
      'Jean',
      'Austin',
      'Alice',
      'Arthur',
      'Kathryn',
      'Lawrence',
      'Gloria',
      'Jesse',
      'Teresa',
      'Dylan',
      'Sara',
      'Aiden',
      'Sofia',
      'Liam',
      'Ava',
      'Ethan',
      'Isabella',
      'Mason',
      'Mia',
      'Noah',
      'Zoe',
      'Lucas',
      'Lily',
      'Jackson',
      'Chloe',
      'Logan',
      'Madison',
      'Oliver',
      'Charlotte',
      'Elijah',
      'Harper',
      'Muhammad',
      'Layla',
      'Alexander',
      'Sophia',
      'Jayden',
      'Maya',
      'William',
      'Avery',
      'James',
      'Evelyn',
      'Benjamin',
      'Abigail',
      'Leo',
      'Ella',
      'Sebastian',
      'Scarlett',
      'Jack',
      'Grace',
      'Theodore',
      'Victoria',
      'Mateo',
      'Riley',
      'Levi',
      'Aria',
      'Isaac',
      'Ellie',
      'Gabriel',
      'Hannah',
      'Julian',
      'Leah',
      'Wyatt',
      'Aubrey',
      'Luke',
      'Stella',
      'Anthony',
      'Natalie',
      'Henry',
      'Zara',
      'Kai',
      'Nora',
      'Jayce',
      'Zoey',
      'Caleb',
      'Hazel',
      'Owen',
      'Lucy',
      'Ryan',
      'Violet',
      'Nathan',
      'Aurora',
      'Eli',
      'Audrey',
      'Luca',
      'Bella',
      'Isaiah',
      'Claire',
      'Josiah',
      'Skylar',
      'David',
      'Paisley',
      'Adam',
      'Savannah',
      'Jaxon',
      'Brooklyn',
      'Miles',
      'Camila',
      'Victor',
      'Aaliyah',
      'Santiago',
      'Allison',
    ]
    const lastNames = [
      'Smith',
      'Johnson',
      'Williams',
      'Jones',
      'Brown',
      'Davis',
      'Miller',
      'Wilson',
      'Moore',
      'Taylor',
      'Anderson',
      'Thomas',
      'Jackson',
      'White',
      'Harris',
      'Martin',
      'Thompson',
      'Garcia',
      'Martinez',
      'Robinson',
      'Clark',
      'Rodriguez',
      'Lewis',
      'Lee',
      'Walker',
      'Hall',
      'Allen',
      'Young',
      'Hernandez',
      'King',
      'Wright',
      'Lopez',
      'Hill',
      'Scott',
      'Green',
      'Adams',
      'Baker',
      'Gonzalez',
      'Nelson',
      'Carter',
      'Mitchell',
      'Perez',
      'Roberts',
      'Turner',
      'Phillips',
      'Campbell',
      'Parker',
      'Evans',
      'Edwards',
      'Collins',
      'Stewart',
      'Sanchez',
      'Morris',
      'Rogers',
      'Reed',
      'Cook',
      'Morgan',
      'Bell',
      'Murphy',
      'Bailey',
      'Rivera',
      'Cooper',
      'Richardson',
      'Cox',
      'Howard',
      'Ward',
      'Torres',
      'Peterson',
      'Gray',
      'Ramirez',
      'James',
      'Watson',
      'Brooks',
      'Kelly',
      'Sanders',
      'Price',
      'Bennett',
      'Wood',
      'Barnes',
      'Ross',
      'Henderson',
      'Coleman',
      'Jenkins',
      'Perry',
      'Powell',
      'Long',
      'Patterson',
      'Hughes',
      'Flores',
      'Washington',
      'Butler',
      'Simmons',
      'Foster',
      'Gonzales',
      'Bryant',
      'Alexander',
      'Russell',
      'Griffin',
      'Diaz',
      'Hayes',
      'Myers',
      'Ford',
      'Hamilton',
      'Graham',
      'Sullivan',
      'Wallace',
      'Woods',
      'Cole',
      'West',
      'Jordan',
      'Owens',
      'Reynolds',
      'Fisher',
      'Ellis',
      'Harrison',
      'Gibson',
      'McDonald',
      'Cruz',
      'Marshall',
      'Ortiz',
      'Gomez',
      'Murray',
      'Freeman',
      'Wells',
      'Webb',
      'Simpson',
      'Stevens',
      'Tucker',
      'Porter',
      'Hunter',
      'Hicks',
      'Crawford',
      'Henry',
      'Boyd',
      'Mason',
      'Morales',
      'Kennedy',
      'Warren',
      'Dixon',
      'Ramos',
      'Reyes',
      'Burns',
      'Gordon',
      'Shaw',
      'Holmes',
      'Rice',
      'Robertson',
      'Hunt',
      'Black',
      'Daniels',
      'Palmer',
      'Mills',
      'Nichols',
      'Grant',
      'Knight',
      'Ferguson',
      'Rose',
      'Stone',
      'Hawkins',
      'Dunn',
      'Perkins',
      'Hudson',
      'Spencer',
      'Gardner',
      'Stephens',
      'Payne',
      'Pierce',
      'Berry',
      'Matthews',
      'Arnold',
      'Wagner',
      'Willis',
      'Ray',
      'Watkins',
      'Olson',
      'Carroll',
      'Duncan',
      'Snyder',
      'Hart',
      'Cunningham',
      'Bradley',
      'Lane',
      'Andrews',
      'Ruiz',
      'Harper',
      'Fox',
      'Riley',
      'Armstrong',
      'Carpenter',
      'Weaver',
      'Greene',
      'Lawrence',
      'Elliott',
      'Chavez',
      'Sims',
      'Austin',
      'Peters',
      'Kelley',
      'Franklin',
      'Lawson',
    ]
    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)]
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)]
    return `${firstName} ${lastName}`
  }
  return {
    departments,
    getPositionForDepartment,
    randomName,
    randomEmail,
    randomPhone,
    randomDate,
    randomAmount,
    expenseTypes,
    expenseSubtypes,
    expenseStatuses,
    locations,
    receiptUrls,
    rejectionReasons,
  }
}
// Generate mock data using our helper functions
const {
  departments,
  getPositionForDepartment,
  randomName,
  randomEmail,
  randomPhone,
  randomDate,
  randomAmount,
  expenseTypes,
  expenseSubtypes,
  expenseStatuses,
  locations,
  receiptUrls,
  rejectionReasons,
} = generateRandomData()
// Generate a large number of employees
const generateEmployees = (count) => {
  const employees = []
  // First, add our fixed employees for testing
  const fixedEmployees = [
    {
      id: 1,
      name: 'John Doe',
      email: 'john.doe@example.com',
      phone: '555-123-4567',
      department: 'Engineering',
      position: 'Software Engineer',
      role: 'employee',
      hireDate: '2021-03-15',
    },
    {
      id: 2,
      name: 'Jane Smith',
      email: 'jane.smith@example.com',
      phone: '555-987-6543',
      department: 'Marketing',
      position: 'Marketing Manager',
      role: 'admin',
      hireDate: '2020-06-22',
    },
    {
      id: 3,
      name: 'Robert Johnson',
      email: 'robert.johnson@example.com',
      phone: '555-456-7890',
      department: 'Finance',
      position: 'Financial Analyst',
      role: 'employee',
      hireDate: '2022-01-10',
    },
    {
      id: 4,
      name: 'Emily Davis',
      email: 'emily.davis@example.com',
      phone: '555-789-0123',
      department: 'HR',
      position: 'HR Specialist',
      role: 'employee',
      hireDate: '2021-09-05',
    },
    {
      id: 5,
      name: 'Michael Wilson',
      email: 'michael.wilson@example.com',
      phone: '555-234-5678',
      department: 'Engineering',
      position: 'Senior Developer',
      role: 'employee',
      hireDate: '2019-11-18',
    },
  ]
  employees.push(...fixedEmployees)
  // Then add randomly generated employees
  for (let i = 6; i <= count; i++) {
    const name = randomName()
    const department =
      departments[Math.floor(Math.random() * departments.length)]
    const position = getPositionForDepartment(department)
    // Determine role (mostly employees, some admins)
    const role = Math.random() < 0.1 ? 'admin' : 'employee'
    // Generate hire date (between 5 years ago and today)
    const today = new Date()
    const fiveYearsAgo = new Date(today)
    fiveYearsAgo.setFullYear(today.getFullYear() - 5)
    const hireDate = randomDate(fiveYearsAgo, today).toISOString().split('T')[0]
    employees.push({
      id: i,
      name,
      email: randomEmail(name),
      phone: randomPhone(),
      department,
      position,
      role,
      hireDate,
    })
  }
  return employees
}
// Generate a large number of expenses
const generateExpenses = (employees, count) => {
  const expenses = []
  // First, add our fixed expenses for testing
  const fixedExpenses = [
    {
      id: 1,
      employeeId: 1,
      date: '2023-05-15',
      submittedDate: '2023-05-16',
      type: 'meal',
      subtype: 'dinner',
      amount: '75.20',
      description: 'Client dinner meeting with Acme Corp',
      location: 'San Francisco, CA',
      status: 'approved',
      receiptUrl: 'https://placehold.co/600x400/png',
    },
    {
      id: 2,
      employeeId: 1,
      date: '2023-05-20',
      submittedDate: '2023-05-21',
      type: 'transportation',
      subtype: 'taxi',
      amount: '32.50',
      description: 'Taxi to airport',
      location: 'San Francisco, CA',
      status: 'pending',
      receiptUrl: 'https://placehold.co/600x400/png',
    },
    {
      id: 3,
      employeeId: 2,
      date: '2023-05-18',
      submittedDate: '2023-05-19',
      type: 'lodging',
      amount: '245.00',
      description: 'Hotel stay for conference',
      location: 'New York, NY',
      status: 'approved',
      receiptUrl: 'https://placehold.co/600x400/png',
    },
    {
      id: 4,
      employeeId: 3,
      date: '2023-05-22',
      submittedDate: '2023-05-23',
      type: 'transportation',
      subtype: 'airfare',
      amount: '450.75',
      description: 'Flight to Chicago for quarterly meeting',
      location: 'Chicago, IL',
      status: 'rejected',
      rejectionReason: 'Missing itinerary details',
      rejectedDate: '2023-05-24',
      receiptUrl: null,
    },
    {
      id: 5,
      employeeId: 1,
      date: '2023-05-25',
      submittedDate: '2023-05-26',
      type: 'meal',
      subtype: 'lunch',
      amount: '28.90',
      description: 'Lunch with team',
      location: 'San Francisco, CA',
      status: 'pending',
      receiptUrl: 'https://placehold.co/600x400/png',
    },
    {
      id: 6,
      employeeId: 4,
      date: '2023-05-19',
      submittedDate: '2023-05-20',
      type: 'other',
      amount: '120.00',
      description: 'Office supplies purchase',
      location: 'Online',
      status: 'approved',
      receiptUrl: 'https://placehold.co/600x400/png',
    },
    {
      id: 7,
      employeeId: 5,
      date: '2023-05-21',
      submittedDate: '2023-05-22',
      type: 'transportation',
      subtype: 'mileage',
      amount: '45.60',
      description: 'Mileage for client visit',
      location: 'San Jose, CA',
      status: 'approved',
      receiptUrl: null,
    },
    {
      id: 8,
      employeeId: 2,
      date: '2023-05-24',
      submittedDate: '2023-05-25',
      type: 'meal',
      subtype: 'breakfast',
      amount: '18.75',
      description: 'Breakfast with potential client',
      location: 'New York, NY',
      status: 'pending',
      receiptUrl: 'https://placehold.co/600x400/png',
    },
    {
      id: 9,
      employeeId: 3,
      date: '2023-05-17',
      submittedDate: '2023-05-18',
      type: 'other',
      amount: '89.99',
      description: 'Software subscription',
      location: 'Online',
      status: 'rejected',
      rejectionReason: 'Personal expense',
      rejectedDate: '2023-05-19',
      receiptUrl: 'https://placehold.co/600x400/png',
    },
    {
      id: 10,
      employeeId: 1,
      date: '2023-05-10',
      submittedDate: '2023-05-11',
      type: 'transportation',
      subtype: 'train',
      amount: '65.00',
      description: 'Train ticket to client meeting',
      location: 'Washington, DC',
      status: 'approved',
      receiptUrl: 'https://placehold.co/600x400/png',
    },
  ]
  expenses.push(...fixedExpenses)
  // Generate more expenses
  for (let i = 11; i <= count; i++) {
    // Pick a random employee
    const employee = employees[Math.floor(Math.random() * employees.length)]
    // Generate expense date (between 1 year ago and today)
    const today = new Date()
    const oneYearAgo = new Date(today)
    oneYearAgo.setFullYear(today.getFullYear() - 1)
    const expenseDate = randomDate(oneYearAgo, today)
    // Format dates
    const date = expenseDate.toISOString().split('T')[0]
    // Generate submitted date (0-3 days after expense date)
    const submittedDate = new Date(expenseDate)
    submittedDate.setDate(expenseDate.getDate() + Math.floor(Math.random() * 4))
    const submittedDateStr = submittedDate.toISOString().split('T')[0]
    // Pick expense type and subtype
    const type = expenseTypes[Math.floor(Math.random() * expenseTypes.length)]
    const subtypes = expenseSubtypes[type] || []
    const subtype =
      subtypes.length > 0
        ? subtypes[Math.floor(Math.random() * subtypes.length)]
        : undefined
    // Generate amount based on type
    let minAmount = 10
    let maxAmount = 100
    if (type === 'lodging') {
      minAmount = 100
      maxAmount = 500
    } else if (type === 'transportation' && subtype === 'airfare') {
      minAmount = 200
      maxAmount = 1200
    } else if (type === 'meal') {
      minAmount = 15
      maxAmount = 150
    } else if (type === 'hardware') {
      minAmount = 50
      maxAmount = 2000
    } else if (type === 'software') {
      minAmount = 20
      maxAmount = 500
    }
    const amount = randomAmount(minAmount, maxAmount)
    // Pick status
    const status =
      expenseStatuses[Math.floor(Math.random() * expenseStatuses.length)]
    // Generate rejection details if rejected
    let rejectionReason, rejectedDate
    if (status === 'rejected') {
      rejectionReason =
        rejectionReasons[Math.floor(Math.random() * rejectionReasons.length)]
      const rejectedDateObj = new Date(submittedDate)
      rejectedDateObj.setDate(
        submittedDate.getDate() + Math.floor(Math.random() * 5) + 1,
      )
      rejectedDate = rejectedDateObj.toISOString().split('T')[0]
    }
    // Pick location
    const location = locations[Math.floor(Math.random() * locations.length)]
    // Generate description based on type
    let description = ''
    switch (type) {
      case 'meal':
        description = `${subtype || 'Meal'} ${Math.random() > 0.5 ? 'with client' : 'with team'}`
        break
      case 'transportation':
        description = `${subtype || 'Transportation'} for ${Math.random() > 0.5 ? 'client meeting' : 'business trip'}`
        break
      case 'lodging':
        description = `${subtype || 'Hotel'} stay for ${Math.random() > 0.5 ? 'conference' : 'business trip'}`
        break
      case 'office':
        description = `Office ${subtype || 'supplies'}`
        break
      case 'software':
        description = `Software ${subtype || 'purchase'} for work`
        break
      case 'hardware':
        description = `${subtype || 'Hardware'} purchase for work`
        break
      case 'entertainment':
        description = `${subtype || 'Entertainment'} for ${Math.random() > 0.5 ? 'clients' : 'team'}`
        break
      case 'travel':
        description = `${subtype || 'Travel'} expenses`
        break
      case 'training':
        description = `${subtype || 'Training'} expenses`
        break
      default:
        description = `${subtype || 'Miscellaneous'} business expense`
    }
    // Add some more detail to the description
    if (Math.random() > 0.7) {
      const details = [
        'for Q1 planning',
        'for annual conference',
        'for team offsite',
        'for project kickoff',
        'for customer visit',
        'for quarterly review',
        'for sales meeting',
        'for product demo',
        'for industry event',
        'for company retreat',
      ]
      description += ' ' + details[Math.floor(Math.random() * details.length)]
    }
    // Pick receipt URL
    const receiptUrl =
      receiptUrls[Math.floor(Math.random() * receiptUrls.length)]
    expenses.push({
      id: i,
      employeeId: employee.id,
      date,
      submittedDate: submittedDateStr,
      type,
      ...(subtype && { subtype }),
      amount,
      description,
      location,
      status,
      ...(rejectionReason && { rejectionReason }),
      ...(rejectedDate && { rejectedDate }),
      receiptUrl,
    })
  }
  return expenses
}
// Generate mock data
export const mockEmployees = generateEmployees(2000) // Generate 2000 employees
export const mockExpenses = generateExpenses(mockEmployees, 5000) // Generate 5000 expenses
// Mock per diem rates
export const mockPerDiemRates = {
  locations: {
    'Washington, DC': {
      lodgingRate: 250,
      mealRate: 76,
      mealBreakdown: {
        breakfast: 16,
        lunch: 17,
        dinner: 31,
        incidentals: 12,
      },
    },
    'New York, NY': {
      lodgingRate: 350,
      mealRate: 86,
      mealBreakdown: {
        breakfast: 18,
        lunch: 19,
        dinner: 36,
        incidentals: 13,
      },
    },
    'San Francisco, CA': {
      lodgingRate: 300,
      mealRate: 81,
      mealBreakdown: {
        breakfast: 17,
        lunch: 18,
        dinner: 34,
        incidentals: 12,
      },
    },
    'Chicago, IL': {
      lodgingRate: 275,
      mealRate: 74,
      mealBreakdown: {
        breakfast: 16,
        lunch: 17,
        dinner: 30,
        incidentals: 11,
      },
    },
  },
  // Get per diem rate for a specific location and date
  getPerDiemRateForDate(location, date) {
    // Validate inputs
    if (!location || !date || !this.locations[location]) {
      console.error('Invalid location or date for per diem lookup', {
        location,
        date,
      })
      return null
    }
    // In a real app, rates might vary by date, but for this mock we'll return the same rate
    return this.locations[location]
  },
  // Validate an expense against per diem rates
  validateExpenseAgainstPerDiem(type, subtype, amount, location, date) {
    // Validate inputs
    if (!location || !date || !this.locations[location]) {
      console.error('Invalid location or date for per diem validation', {
        location,
        date,
      })
      return { valid: true } // Default to valid if we can't validate
    }
    const perDiemRate = this.getPerDiemRateForDate(location, date)
    if (!perDiemRate) {
      return { valid: true }
    }
    // Check meal expenses
    if (type === 'meal' && subtype) {
      const allowance = perDiemRate.mealBreakdown[subtype]
      if (allowance && amount > allowance) {
        return {
          valid: false,
          allowance,
          message: `This ${subtype} expense exceeds the per diem limit of $${allowance}`,
        }
      }
    }
    // Check lodging expenses
    if (type === 'lodging') {
      const allowance = perDiemRate.lodgingRate
      if (amount > allowance) {
        return {
          valid: false,
          allowance,
          message: `This lodging expense exceeds the per diem limit of $${allowance}`,
        }
      }
    }
    return { valid: true }
  },
}
// Mock chatbot responses
export const mockChatbotResponses = {
  responses: {
    meal: "According to Sprinklenet's expense policy, meal expenses are reimbursed based on GSA per diem rates for your location. The current rates are: Breakfast $17, Lunch $18, Dinner $34, and Incidentals $10 per day for your location. You must submit itemized receipts for all meal expenses.",
    receipt:
      'Receipts are required for all expenses over $25. For meals, an itemized receipt showing all items purchased is required, not just the credit card slip. For airfare, you need both the receipt and boarding pass. Digital copies (clear photos or scans) are acceptable.',
    deadline:
      'Expense reports should be submitted within 30 days of incurring the expense. Expenses submitted after 60 days may not be reimbursed without VP approval. The finance team processes approved expenses every Friday, with reimbursements typically appearing in your bank account by the following Wednesday.',
    approval:
      "After submission, your expense report will be reviewed by your direct manager. Expenses over $500 require additional approval from the department head. The finance team performs a final compliance check before processing payment. You'll receive notifications at each approval stage via email and in the app.",
    lodging:
      'Lodging expenses should not exceed GSA per diem rates for the location, which is $251 per night for Washington DC. Incidental charges like room service, mini-bar, or movies are not reimbursable. You must submit the detailed hotel folio showing the room rate and taxes separately.',
    transportation:
      'For air travel, book economy class at least 14 days in advance when possible. For rental cars, choose economy or compact vehicles. Rideshare services are preferred over taxis when available. Personal vehicle mileage is reimbursed at the current IRS rate of $0.655 per mile.',
    international:
      "International travel requires pre-approval through the Travel Authorization form. Exchange rates are calculated based on the date of the expense using the company's financial system. Submit receipts in the original currency along with the credit card statement showing the USD amount charged.",
    alcohol:
      'Alcoholic beverages are not reimbursable as standalone expenses. If included with a business meal, alcohol should be reasonable and not exceed 20% of the total meal cost. Itemized receipts are required to distinguish between food and alcohol charges.',
    attendees:
      'For business meals with external parties, you must list all attendees, their organizations, and the business purpose. For internal team meals, list all employee names and the business purpose. Team meals without a specific business purpose are not reimbursable.',
    perdiem:
      'Sprinklenet uses GSA per diem rates, which vary by location and time of year. The rates include separate allowances for lodging and meals/incidentals. You can check current rates for your destination in this app or on the GSA website. Expenses exceeding per diem require justification and may not be fully reimbursed.',
  },
  // Get response based on user query
  getResponse(query) {
    query = query.toLowerCase()
    if (
      query.includes('meal') ||
      query.includes('food') ||
      query.includes('breakfast') ||
      query.includes('lunch') ||
      query.includes('dinner')
    ) {
      return this.responses.meal
    } else if (query.includes('receipt') || query.includes('documentation')) {
      return this.responses.receipt
    } else if (
      query.includes('deadline') ||
      query.includes('time') ||
      query.includes('submit') ||
      query.includes('late')
    ) {
      return this.responses.deadline
    } else if (
      query.includes('approval') ||
      query.includes('process') ||
      query.includes('approve')
    ) {
      return this.responses.approval
    } else if (
      query.includes('hotel') ||
      query.includes('lodging') ||
      query.includes('stay')
    ) {
      return this.responses.lodging
    } else if (
      query.includes('transportation') ||
      query.includes('travel') ||
      query.includes('flight') ||
      query.includes('car') ||
      query.includes('taxi')
    ) {
      return this.responses.transportation
    } else if (
      query.includes('international') ||
      query.includes('foreign') ||
      query.includes('overseas') ||
      query.includes('abroad')
    ) {
      return this.responses.international
    } else if (
      query.includes('alcohol') ||
      query.includes('drink') ||
      query.includes('beer') ||
      query.includes('wine')
    ) {
      return this.responses.alcohol
    } else if (
      query.includes('attendee') ||
      query.includes('guest') ||
      query.includes('client') ||
      query.includes('team')
    ) {
      return this.responses.attendees
    } else if (
      query.includes('per diem') ||
      query.includes('allowance') ||
      query.includes('limit') ||
      query.includes('maximum')
    ) {
      return this.responses.perdiem
    } else {
      return "I don't have specific information about that topic. Please try asking about meal limits, receipts, submission deadlines, approval process, lodging, transportation, international travel, alcohol policy, attendees, or per diem rates. You can also contact the finance team at finance@sprinklenet.com for more specific questions."
    }
  },
}
// Mock Policy Documents
export const mockPolicyDocuments = [
  {
    id: 1,
    name: 'Expense Policy Handbook.pdf',
    size: 1250000, // 1.25 MB
    type: 'application/pdf',
    uploadDate: '2023-05-15T10:30:00Z',
    description: 'Corporate expense policy guidelines and procedures',
    category: 'Finance',
  },
  {
    id: 2,
    name: 'Travel Guidelines.pdf',
    size: 890000, // 890 KB
    type: 'application/pdf',
    uploadDate: '2023-05-20T14:45:00Z',
    description:
      'Business travel guidelines including booking and reimbursement',
    category: 'Travel',
  },
  {
    id: 3,
    name: 'Per Diem Rates 2023.pdf',
    size: 450000, // 450 KB
    type: 'application/pdf',
    uploadDate: '2023-06-01T09:15:00Z',
    description: 'Current fiscal year per diem rates by location',
    category: 'Finance',
  },
  {
    id: 4,
    name: 'International Travel Policy.pdf',
    size: 720000, // 720 KB
    type: 'application/pdf',
    uploadDate: '2023-06-10T11:20:00Z',
    description: 'Guidelines for international business travel and expenses',
    category: 'Travel',
  },
  {
    id: 5,
    name: 'Receipt Requirements.pdf',
    size: 380000, // 380 KB
    type: 'application/pdf',
    uploadDate: '2023-06-15T13:40:00Z',
    description: 'Detailed requirements for expense receipts and documentation',
    category: 'Finance',
  },
  {
    id: 6,
    name: 'Company Card Usage Policy.pdf',
    size: 520000, // 520 KB
    type: 'application/pdf',
    uploadDate: '2023-06-20T15:30:00Z',
    description: 'Rules and guidelines for corporate credit card usage',
    category: 'Finance',
  },
  {
    id: 7,
    name: 'Meal and Entertainment Guidelines.pdf',
    size: 640000, // 640 KB
    type: 'application/pdf',
    uploadDate: '2023-06-25T09:45:00Z',
    description: 'Policies for business meals and entertainment expenses',
    category: 'Finance',
  },
]
